/**
 * platform/config.js
 *
 * Single source of truth for platform-level branding & routing.
 * Change PLATFORM_NAME and PLATFORM_DOMAIN here to rebrand the entire platform.
 */

export const PLATFORM_NAME = "Rewardlix";
export const PLATFORM_DOMAIN = "rewardlix.com";
export const SHOP_SUBDOMAIN_SUFFIX =
  import.meta.env.VITE_SHOP_SUBDOMAIN_SUFFIX || "";

/**
 * URL pattern for shop customer landing pages.
 * Strategy: subdomain — {slug}{suffix}.rewardlix.com
 *
 * NOTE: Wildcard subdomain DNS (*.rewardlix.com → Hosting)
 * must be configured in your DNS provider + Firebase Hosting custom domains.
 * During development the app falls back to path-based routing: /s/{slug}
 */
export const SHOP_URL_STRATEGY = "subdomain"; // "subdomain" | "path"

export function shopSubdomainLabel(slug) {
  return `${slug}${SHOP_SUBDOMAIN_SUFFIX}`;
}

export function shopDomainSuffixLabel() {
  return `${SHOP_SUBDOMAIN_SUFFIX}.${PLATFORM_DOMAIN}`;
}

export function shopCustomDomain(slug) {
  return `${shopSubdomainLabel(slug)}.${PLATFORM_DOMAIN}`;
}

export function getShopSlugFromSubdomain(subdomain) {
  if (!subdomain) {
    return null;
  }

  if (!SHOP_SUBDOMAIN_SUFFIX) {
    return subdomain;
  }

  if (!subdomain.endsWith(SHOP_SUBDOMAIN_SUFFIX)) {
    return null;
  }

  return subdomain.slice(0, -SHOP_SUBDOMAIN_SUFFIX.length) || null;
}

/** Build the public-facing URL for a shop slug */
export function shopUrl(slug) {
  if (SHOP_URL_STRATEGY === "subdomain") {
    return `https://${shopCustomDomain(slug)}`;
  }
  return `https://${PLATFORM_DOMAIN}/s/${slug}`;
}

/** Build a pretty display handle for a shop (e.g. psalidi.rewardlix.com) */
export function shopHandle(slug) {
  return shopCustomDomain(slug);
}

/**
 * Platform-reserved subdomains that must never be treated as shop slugs.
 * Add any staging / ops / infrastructure subdomains here.
 */
const RESERVED_SUBDOMAINS = new Set([
  "staging",
  "preview",
  "www",
  "app",
  "api",
  "admin",
  "dashboard",
  "status",
  "mail",
  "smtp",
]);

/**
 * Returns the shop slug for the current browser subdomain, or null.
 * Works in both the main SPA (router.js) and customer views.
 * Returns null on localhost / the main platform domain (safe to call anywhere).
 * Returns null for reserved platform subdomains (e.g. "staging").
 */
export function getActiveSubdomainSlug(hostname = window.location.hostname) {
  const suffix = `.${PLATFORM_DOMAIN}`;
  if (!hostname.endsWith(suffix)) return null;
  const subdomain = hostname.slice(0, -suffix.length);
  if (!subdomain || subdomain.includes(".")) return null;
  if (RESERVED_SUBDOMAINS.has(subdomain.toLowerCase())) return null;
  return getShopSlugFromSubdomain(subdomain) || null;
}
